import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { RequirementsChecklist } from "@/components/WorkerRequirements";
import { WORKER_CATEGORIES, EMPLOYMENT_CONTEXTS, WORKER_LOCATIONS, REGULATED_VALUES, RECOGNITION_STATUSES, suggestCategory } from "@/lib/workerTypes";
import api, { formatApiErrorDetail } from "@/lib/api";

const empty = {
  first_name: "", last_name: "", email: "", phone: "", date_of_birth: "", place_of_birth: "", nationality: "",
  worker_category: "", employment_context: "DIRECT_EMPLOYEE", worker_location_status: "UNKNOWN",
  position_title: "", profession: "", job_title: "", employment_start_date: "",
  residence_status: "", permit_type: "", permit_valid_until: "", work_authorization_status: "",
  visa_type: "", visa_valid_until: "", issuing_authority: "",
  foreign_employer: "", home_country: "", posting_start: "", posting_end: "", a1_valid_from: "", a1_valid_until: "", sending_institution: "",
  regulated_profession: "UNKNOWN", recognition_status: "UNKNOWN", recognition_authority: "",
};

function Field({ label, children }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}

export default function EmployeeWizard() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [reqs, setReqs] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const TOTAL = 6;

  const suggestion = suggestCategory(f.nationality);
  const catLabel = (v) => t(`wc.${v}`);

  const canNext = () => {
    if (step === 1) return f.first_name && f.last_name;
    if (step === 2) return !!f.worker_category;
    if (step === 3) return !!f.employment_context;
    if (step === 4) return !!f.worker_location_status;
    return true;
  };

  const submit = async () => {
    if (saving || created) return;
    setSaving(true);
    const payload = { ...f };
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    if (!payload.email) delete payload.email;
    try {
      const { data } = await api.post("/employer/employees", payload);
      setCreated(data);
      const r = await api.get(`/employer/employees/${data.id}/requirements`);
      setReqs(r.data);
      toast.success(t("p2.created"));
      setStep(6);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="employee-wizard" className="max-w-3xl">
      <Link to="/app/employer/employees" className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("f.back")}</Link>
      <h1 className="mb-1 font-heading text-2xl font-extrabold text-navy sm:text-3xl">{t("p2.wizTitle")}</h1>
      <p className="mb-4 text-sm text-slate-500">{t("p2.step", { n: step, total: TOTAL })}</p>
      <div className="mb-6 h-1.5 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-cyan transition-all" style={{ width: `${(step / TOTAL) * 100}%` }} /></div>

      <div className="card-soft p-6">
        {step === 1 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <h3 className="sm:col-span-2 font-heading text-lg font-bold text-navy">{t("p2.s1")}</h3>
            <Field label={t("f.firstName")}><Input value={f.first_name} onChange={(e) => set("first_name")(e.target.value)} data-testid="wiz-firstname" /></Field>
            <Field label={t("f.lastName")}><Input value={f.last_name} onChange={(e) => set("last_name")(e.target.value)} data-testid="wiz-lastname" /></Field>
            <Field label={t("f.email")}><Input type="email" value={f.email} onChange={(e) => set("email")(e.target.value)} data-testid="wiz-email" /></Field>
            <Field label={t("f.phone")}><Input value={f.phone} onChange={(e) => set("phone")(e.target.value)} /></Field>
            <Field label={t("f.dob")}><DatePicker value={f.date_of_birth} onChange={set("date_of_birth")} testid="wiz-dob" /></Field>
            <Field label={t("p2.placeOfBirth")}><Input value={f.place_of_birth} onChange={(e) => set("place_of_birth")(e.target.value)} /></Field>
            <Field label={t("f.nationality")}><Input value={f.nationality} onChange={(e) => set("nationality")(e.target.value)} data-testid="wiz-nationality" /></Field>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-bold text-navy">{t("p2.s2")}</h3>
            {f.nationality && suggestion !== "NOT_CLASSIFIED" && (
              <div className="flex items-center justify-between rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-2.5" data-testid="wiz-suggestion">
                <span className="flex items-center gap-2 text-sm text-cyan-800"><Sparkles className="h-4 w-4" /> {t("p2.suggest")}: <b>{catLabel(suggestion)}</b></span>
                <Button size="sm" variant="outline" className="rounded-full bg-white" onClick={() => set("worker_category")(suggestion)} data-testid="wiz-apply-suggestion">{t("p2.applySuggest")}</Button>
              </div>
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              {WORKER_CATEGORIES.filter((c) => c !== "NOT_CLASSIFIED").map((c) => (
                <button key={c} type="button" onClick={() => set("worker_category")(c)} data-testid={`wiz-cat-${c}`}
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all ${f.worker_category === c ? "border-cyan bg-cyan-50 text-navy" : "border-slate-200 text-slate-600 hover:border-cyan-200"}`}>
                  {catLabel(c)}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-bold text-navy">{t("p2.s3")}</h3>
            <div className="grid gap-2 sm:grid-cols-3">
              {EMPLOYMENT_CONTEXTS.map((c) => (
                <button key={c} type="button" onClick={() => set("employment_context")(c)} data-testid={`wiz-ctx-${c}`}
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all ${f.employment_context === c ? "border-cyan bg-cyan-50 text-navy" : "border-slate-200 text-slate-600 hover:border-cyan-200"}`}>
                  {t(`p2.ctx.${c}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h3 className="font-heading text-lg font-bold text-navy">{t("p2.s4")}</h3>
            <div className="grid gap-2 sm:grid-cols-3">
              {WORKER_LOCATIONS.map((c) => (
                <button key={c} type="button" onClick={() => set("worker_location_status")(c)} data-testid={`wiz-loc-${c}`}
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all ${f.worker_location_status === c ? "border-cyan bg-cyan-50 text-navy" : "border-slate-200 text-slate-600 hover:border-cyan-200"}`}>
                  {t(`p2.loc.${c}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <h3 className="sm:col-span-2 font-heading text-lg font-bold text-navy">{t("p2.s5")}</h3>
              <Field label={t("p2.positionTitle")}><Input value={f.position_title} onChange={(e) => set("position_title")(e.target.value)} data-testid="wiz-position" /></Field>
              <Field label={t("p2.profession")}><Input value={f.profession} onChange={(e) => set("profession")(e.target.value)} /></Field>
              <Field label={t("f.startDate")}><DatePicker value={f.employment_start_date} onChange={set("employment_start_date")} /></Field>
              <Field label={t("p2.regulatedProfession")}>
                <Select value={f.regulated_profession} onValueChange={set("regulated_profession")}><SelectTrigger data-testid="wiz-regulated"><SelectValue /></SelectTrigger>
                  <SelectContent>{REGULATED_VALUES.map((v) => <SelectItem key={v} value={v}>{t(`p2.reg.${v}`)}</SelectItem>)}</SelectContent></Select>
              </Field>
            </div>

            {f.worker_category === "NON_EU" && (
              <div className="grid gap-4 rounded-xl border border-slate-200 p-4 sm:grid-cols-2" data-testid="wiz-noneu-fields">
                <h4 className="sm:col-span-2 text-sm font-bold text-navy">{t("p2.sec.RESIDENCE_WORK")}</h4>
                <Field label={t("f.residenceStatus")}><Input value={f.residence_status} onChange={(e) => set("residence_status")(e.target.value)} placeholder="§18a AufenthG" /></Field>
                <Field label={t("f.permitType")}><Input value={f.permit_type} onChange={(e) => set("permit_type")(e.target.value)} /></Field>
                <Field label={t("f.workAuth")}><Input value={f.work_authorization_status} onChange={(e) => set("work_authorization_status")(e.target.value)} /></Field>
                <Field label={t("f.permitUntil")}><DatePicker value={f.permit_valid_until} onChange={set("permit_valid_until")} /></Field>
                {f.worker_location_status === "ABROAD" && <>
                  <Field label={t("p2.visaType")}><Input value={f.visa_type} onChange={(e) => set("visa_type")(e.target.value)} /></Field>
                  <Field label={t("p2.visaUntil")}><DatePicker value={f.visa_valid_until} onChange={set("visa_valid_until")} /></Field>
                </>}
              </div>
            )}

            {f.employment_context === "POSTED_WORKER" && (
              <div className="grid gap-4 rounded-xl border border-slate-200 p-4 sm:grid-cols-2" data-testid="wiz-posting-fields">
                <h4 className="sm:col-span-2 text-sm font-bold text-navy">{t("p2.sec.POSTING")}</h4>
                <Field label={t("p2.foreignEmployer")}><Input value={f.foreign_employer} onChange={(e) => set("foreign_employer")(e.target.value)} /></Field>
                <Field label={t("p2.homeCountry")}><Input value={f.home_country} onChange={(e) => set("home_country")(e.target.value)} /></Field>
                <Field label={t("p2.postingStart")}><DatePicker value={f.posting_start} onChange={set("posting_start")} /></Field>
                <Field label={t("p2.postingEnd")}><DatePicker value={f.posting_end} onChange={set("posting_end")} /></Field>
                <Field label={t("p2.a1Until")}><DatePicker value={f.a1_valid_until} onChange={set("a1_valid_until")} /></Field>
                <Field label={t("p2.sendingInstitution")}><Input value={f.sending_institution} onChange={(e) => set("sending_institution")(e.target.value)} /></Field>
              </div>
            )}

            {f.regulated_profession === "YES" && (
              <div className="grid gap-4 rounded-xl border border-slate-200 p-4 sm:grid-cols-2" data-testid="wiz-recognition-fields">
                <h4 className="sm:col-span-2 text-sm font-bold text-navy">{t("p2.sec.RECOGNITION")}</h4>
                <Field label={t("p2.recognitionStatus")}>
                  <Select value={f.recognition_status} onValueChange={set("recognition_status")}><SelectTrigger data-testid="wiz-recognition"><SelectValue /></SelectTrigger>
                    <SelectContent>{RECOGNITION_STATUSES.map((v) => <SelectItem key={v} value={v}>{t(`p2.rec.${v}`)}</SelectItem>)}</SelectContent></Select>
                </Field>
                <Field label={t("p2.recognitionAuthority")}><Input value={f.recognition_authority} onChange={(e) => set("recognition_authority")(e.target.value)} /></Field>
              </div>
            )}
          </div>
        )}

        {step === 6 && created && (
          <div className="space-y-5" data-testid="wiz-checklist-step">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><Check className="h-5 w-5" /></span>
              <div><div className="font-heading text-lg font-bold text-navy">{created.first_name} {created.last_name}</div><div className="text-xs text-slate-400">Worker ID: {created.worker_id}</div></div>
            </div>
            <div><div className="mb-2 font-heading font-bold text-navy">{t("p2.s6")}</div><RequirementsChecklist reqs={reqs} /></div>
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-between">
        {step > 1 && step < 6 ? <Button variant="outline" className="rounded-full" onClick={() => setStep((s) => s - 1)} data-testid="wiz-prev"><ArrowLeft className="mr-1 h-4 w-4" /> {t("p2.prev")}</Button> : <span />}
        {step < 5 && <Button className="rounded-full bg-navy text-white hover:bg-navy-muted" disabled={!canNext()} onClick={() => setStep((s) => s + 1)} data-testid="wiz-next">{t("p2.next")} <ArrowRight className="ml-1 h-4 w-4" /></Button>}
        {step === 5 && <Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" disabled={saving} onClick={submit} data-testid="wiz-finish">{saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("f.saving")}</> : t("p2.finish")}</Button>}
        {step === 6 && <Button className="rounded-full bg-navy text-white" onClick={() => nav(`/app/employer/employees/${created.id}`)} data-testid="wiz-godetail">{t("p2.goDetail")} <ArrowRight className="ml-1 h-4 w-4" /></Button>}
      </div>
    </div>
  );
}
